export type RecoveryEvidenceLevel = "established" | "supportive" | "not_proven" | "medical_guidance";

export type RecoverySource = {
  id: string;
  organization: string;
  title: string;
  url: string;
  checkedAt: "2026-08-09";
};

export type RecoveryCard = {
  id: string;
  title: string;
  summary: string;
  detail: string;
  evidenceLevel: RecoveryEvidenceLevel;
  sourceIds: string[];
};

export const recoveryEvidenceLabels: Record<RecoveryEvidenceLevel, { label: string; description: string }> = {
  established: { label: "확실함", description: "공공 보건기관의 일관된 안내" },
  supportive: { label: "증상 보조", description: "수분·식사·휴식에는 도움, 알코올 제거를 빠르게 하지는 않음" },
  not_proven: { label: "입증 부족", description: "숙취 개선 연구의 질과 반복 검증이 부족함" },
  medical_guidance: { label: "의료진과 확인", description: "약·결핍·기저질환에 따라 판단이 달라짐" },
};

export const recoverySources: RecoverySource[] = [
  { id:"niaaa-hangovers", organization:"미국 국립알코올남용·중독연구소(NIAAA)", title:"Hangovers", url:"https://www.niaaa.nih.gov/publications/brochures-and-fact-sheets/hangovers", checkedAt:"2026-08-09" },
  { id:"niaaa-basics", organization:"NIAAA", title:"The Basics: Defining How Much Alcohol is Too Much", url:"https://www.niaaa.nih.gov/health-professionals-communities/core-resource-on-alcohol/basics-defining-how-much-alcohol-too-much", checkedAt:"2026-08-09" },
  { id:"niaaa-overdose", organization:"NIAAA", title:"Alcohol Overdose", url:"https://www.niaaa.nih.gov/alcohols-effects-health/alcohol-topics/health-topics-alcohol-overdose", checkedAt:"2026-08-09" },
  { id:"niaaa-medicine", organization:"NIAAA", title:"Alcohol-Medication Interactions", url:"https://www.niaaa.nih.gov/health-professionals-communities/core-resource-on-alcohol/alcohol-medication-interactions-potentially-dangerous-mixes", checkedAt:"2026-08-09" },
  { id:"nih-thiamin", organization:"미국 국립보건원 영양보충제실(NIH ODS)", title:"Thiamin Fact Sheet for Consumers", url:"https://ods.od.nih.gov/factsheets/Thiamin-Consumer/", checkedAt:"2026-08-09" },
  { id:"pubmed-review", organization:"PubMed 등재 체계적 문헌고찰", title:"Hangover interventions: systematic review of placebo-controlled trials", url:"https://pubmed.ncbi.nlm.nih.gov/34972259/", checkedAt:"2026-08-09" },
  { id:"kdca-myths", organization:"질병관리청 국가건강정보포털", title:"흔한 음주 상식 바로잡기", url:"https://health.kdca.go.kr/healthinfo/biz/health/ntcnInfo/healthSourc/thtimtCntnts/thtimtCntntsView.do?thtimt_cntnts_sn=79", checkedAt:"2026-08-09" },
  { id:"mfds-claims", organization:"식품의약품안전처", title:"2025년 숙취해소 표시·광고 실증제", url:"https://www.mfds.go.kr/files/upload/etc/2025_mfds_1.pdf", checkedAt:"2026-08-09" },
  { id:"who-alcohol", organization:"세계보건기구(WHO)", title:"Alcohol fact sheet", url:"https://www.who.int/news-room/fact-sheets/detail/alcohol", checkedAt:"2026-08-09" },
  { id:"egen-emergency", organization:"중앙응급의료센터 E-GEN", title:"응급똑똑과 119·응급실 안내", url:"https://www.e-gen.or.kr/nemc/notice_view.do?brdctsno=12899&upperfixyn=Y", checkedAt:"2026-08-09" },
];

export const recoveryPrinciples: RecoveryCard[] = [
  {
    id:"time",
    title:"술을 깨는 지름길은 없어요",
    summary:"커피·찬물 샤워·운동·해장술은 알코올 처리 속도를 높이지 않습니다.",
    detail:"몸은 알코올을 일정한 속도로 처리합니다. 정신이 또렷해진 느낌과 실제 판단력·협응력 회복은 다를 수 있으므로 운전과 위험 작업은 하지 않습니다.",
    evidenceLevel:"established",
    sourceIds:["niaaa-hangovers","niaaa-basics"],
  },
  {
    id:"stop",
    title:"더 마시지 않는 게 첫 행동이에요",
    summary:"양이 늘수록 다음날 증상과 급성 위험이 커집니다.",
    detail:"해장술은 잠시 덜 불편하게 느껴질 수 있어도 회복을 늦출 수 있습니다. 지금 잔을 멈추고 안전하게 귀가할 방법을 정하세요.",
    evidenceLevel:"established",
    sourceIds:["niaaa-hangovers","who-alcohol"],
  },
  {
    id:"support",
    title:"물·식사·휴식은 회복을 버티게 도와요",
    summary:"갈증·공복·피로를 덜 수 있지만 술을 빨리 분해하는 해독법은 아닙니다.",
    detail:"한 번에 많이 마시기보다 물을 조금씩 마시고, 속이 받으면 평소 먹던 음식을 소량 드세요. 계속 토하거나 의식이 흐리면 먹이거나 재우지 말고 응급 도움을 요청합니다.",
    evidenceLevel:"supportive",
    sourceIds:["niaaa-hangovers","niaaa-overdose"],
  },
];

export const recoveryTimeline = [
  { id:"now", label:"지금 마시는 중", title:"잔을 멈추고 귀가부터 정하기", actions:["술 대신 물이나 무알코올 음료를 조금씩 마시기", "속이 괜찮으면 평소 먹던 음식 소량 먹기", "직접 운전하지 않고 일행과 안전한 귀가 수단 정하기", "수면제·진정제·진통제를 임의로 섞지 않기"] },
  { id:"night", label:"잠들기 전", title:"의식과 호흡을 먼저 확인하기", actions:["또렷하게 대화되고 정상적으로 호흡하는지 확인하기", "심하게 취한 사람을 혼자 두지 않기", "깨우기 어렵거나 반복 구토·호흡 이상이면 바로 119", "괜찮아 보여도 커피·찬물 샤워로 억지로 깨우지 않기"] },
  { id:"tomorrow", label:"다음날", title:"수분·평소 식사·휴식으로 회복하기", actions:["물은 갈증에 맞춰 나눠 마시기", "속 상태에 맞는 가벼운 식사부터 시작하기", "숙취 중 운전·기계 조작·중요한 돌봄 피하기", "증상이 24시간 넘게 심하거나 악화되면 진료받기"] },
] as const;

export const recoveryFoods: RecoveryCard[] = [
  { id:"water", title:"물", summary:"갈증이 있으면 조금씩 나눠 마셔요.", detail:"수분 섭취는 탈수감 완화에 도움을 줄 수 있지만 혈중 알코올이나 뇌 회복 속도를 앞당기지는 않습니다.", evidenceLevel:"supportive", sourceIds:["niaaa-hangovers"] },
  { id:"clear", title:"죽·밥·토스트·맑은 국물", summary:"속이 예민할 때 부담이 덜한 평소 음식을 골라요.", detail:"음식은 에너지와 수분을 보충하는 선택입니다. 특정 국이나 재료를 해독제로 표현하지 않으며, 짠 국물은 개인의 혈압·부종 상태에 맞춥니다.", evidenceLevel:"supportive", sourceIds:["niaaa-hangovers","kdca-myths"] },
  { id:"regular", title:"달걀·두부·생선·과일", summary:"먹을 수 있다면 평소의 균형 잡힌 한 끼로 돌아가요.", detail:"단백질·과일 자체가 술을 빨리 깨게 한다는 근거는 없습니다. 구토가 없고 속이 받을 때 식사 구성으로 선택하세요.", evidenceLevel:"supportive", sourceIds:["niaaa-hangovers","nih-thiamin"] },
  { id:"spicy", title:"매운 국물·기름진 음식", summary:"당길 수 있지만 속쓰림이 있으면 먼저 피하세요.", detail:"맵고 기름진 음식이 알코올 분해를 빠르게 한다는 근거는 없습니다. 위가 자극된 상태라면 증상을 더 불편하게 만들 수 있어 취향과 속 상태로 고릅니다.", evidenceLevel:"not_proven", sourceIds:["niaaa-hangovers"] },
  { id:"electrolyte", title:"이온·전해질 음료", summary:"마시기 편하면 선택할 수 있지만 필수 해독 음료는 아니에요.", detail:"숙취 강도와 전해질 변화의 연관성, 전해질 보충이 숙취를 줄이는 효과는 확인되지 않았습니다. 당·나트륨 제한이 있다면 성분표를 확인하세요.", evidenceLevel:"not_proven", sourceIds:["niaaa-hangovers"] },
];

export const recoverySupplements: RecoveryCard[] = [
  { id:"vitamins", title:"비타민 B군·비타민 C", summary:"술 깨는 속도를 높인다는 근거는 없습니다.", detail:"평소 영양 섭취나 확인된 결핍을 보충하는 것과 급성 숙취를 없애는 것은 다른 문제입니다. 여러 제품을 중복 복용하지 말고 필요하면 약사·의료진과 확인하세요.", evidenceLevel:"not_proven", sourceIds:["niaaa-hangovers","nih-thiamin"] },
  { id:"thiamin", title:"비타민 B1(티아민)", summary:"알코올 의존이 있으면 결핍 위험이 커질 수 있지만 자가 숙취 처방은 아니에요.", detail:"식사를 거의 못 하거나 장기간 많은 양을 마시는 사람의 결핍은 의료 평가가 필요한 문제입니다. 급성 숙취용 고용량 복용법을 이 서비스가 제안하지 않습니다.", evidenceLevel:"medical_guidance", sourceIds:["nih-thiamin"] },
  { id:"extracts", title:"헛개·홍삼·DHM·L-시스테인·혼합 분말", summary:"작은 단일 연구가 있어도 전체 근거의 확실성은 매우 낮습니다.", detail:"2021년 체계적 문헌고찰은 서로 다른 21개 연구의 표본과 방법에 한계가 커 모든 효능 근거를 매우 낮은 질로 평가했습니다. 제품 표시를 운전 가능이나 알코올 제거 완료로 해석하지 마세요.", evidenceLevel:"not_proven", sourceIds:["pubmed-review","mfds-claims"] },
  { id:"medicine", title:"두통약·감기약·수면제", summary:"성분을 모른 채 술과 함께 복용하지 마세요.", detail:"아세트아미노펜은 간 손상 위험, 아스피린·이부프로펜 같은 소염진통제는 위장 자극·출혈 위험이 문제될 수 있습니다. 복용 중인 약이 있으면 라벨을 읽고 약사·의료진에게 확인하세요.", evidenceLevel:"medical_guidance", sourceIds:["niaaa-hangovers","niaaa-medicine"] },
];

export const recoveryMyths = [
  { myth:"진한 커피를 마시면 술이 빨리 깬다", fact:"잠이 덜 오는 느낌은 생길 수 있지만 알코올 처리와 판단력 회복은 빨라지지 않습니다.", sourceIds:["niaaa-hangovers","niaaa-basics"] },
  { myth:"찬물 샤워나 땀 빼기로 깨울 수 있다", fact:"알코올 중독을 되돌리지 못하며, 심하게 취한 사람에게는 넘어짐·저체온 같은 위험을 더할 수 있습니다.", sourceIds:["niaaa-overdose"] },
  { myth:"해장술 한 잔이면 회복된다", fact:"증상을 잠시 가릴 수 있어도 불편함을 더 오래 끌 수 있습니다.", sourceIds:["niaaa-hangovers"] },
  { myth:"숙취해소 표시가 있으면 운전해도 된다", fact:"식품의 표시 근거와 실제 알코올 제거·운전 판단은 별개입니다. 이 서비스는 BAC나 운전 가능 시간을 계산하지 않습니다.", sourceIds:["mfds-claims","niaaa-hangovers"] },
] as const;

export const alcoholOverdoseSigns = [
  "깨워도 반응이 없거나 의식을 유지하지 못함",
  "심한 혼란·멍함",
  "반복해서 토함",
  "경련",
  "호흡이 매우 느리거나 10초 이상 불규칙하게 멎음",
  "피부가 차갑고 축축하거나 창백·푸르게 보임",
  "체온이 비정상적으로 낮음",
] as const;

export const alcoholOverdoseActions = [
  "증상이 모두 나타날 때까지 기다리지 말고 119에 전화합니다.",
  "사람을 혼자 두지 않고 호흡과 반응을 계속 확인합니다.",
  "토하면 앞으로 숙이게 하고, 의식이 없지만 호흡하면 옆으로 눕혀 기도가 막히지 않게 합니다.",
  "커피·찬물 샤워·걷기·억지로 먹이기·토하게 하기를 시도하지 않습니다.",
  "무엇을 얼마나 마셨는지와 복용 약물을 아는 범위에서 구조대에 알립니다.",
] as const;

export function recoverySourceById(id: string) {
  return recoverySources.find((source) => source.id === id);
}
